/**
 * PlanCard — the clinic's real plan (clinics.plan) as its own block.
 * Plan chips (SYSTEM_PLANS) · monthly list price · churn marker ·
 * optional "פעיל עד" (clinic.planEndDate — nightly cron reverts the clinic to
 * an expired trial once it passes; empty = never). Own save via
 * updateClinicPlanDirectly + setClinicPlanEndDate. Switching plans while limit
 * overrides exist asks whether to keep them (shared draft from usePlanOverrides).
 */

import { useEffect, useState } from "react";
import { Check, Flag, FloppyDisk, Info, X } from "@phosphor-icons/react";
import ConfirmDialog from "../../../../../components/ui/ConfirmDialog";
import DatePicker from "../../../../../components/ui/DatePicker";
import HoverTip from "../../../../../components/ui/HoverTip";
import { useToast } from "../../../../../contexts/ToastContext";
import {
  useSetClinicPlanEndDateMutation,
  useUpdateClinicPlanDirectlyMutation,
} from "../../../../../store/services/nivApi";
import { getSystemPlansArray, SYSTEM_PLANS } from "../../../../../services/nivFirebase";
import { PLAN_END_TOOLTIP } from "../../shared/nivDerived";
import { formatCurrency, formatDate, planToneClass } from "../clinicDetailConstants";

const isoToDateKey = (iso) => (iso ? String(iso).split("T")[0] : "");

export default function PlanCard({ clinic, overrides, refetch }) {
  const { showSuccess, showError } = useToast();
  const [updatePlanDirectly, { isLoading: savingPlan }] = useUpdateClinicPlanDirectlyMutation();
  const [setPlanEndDate, { isLoading: savingEnd }] = useSetClinicPlanEndDateMutation();
  const isLoading = savingPlan || savingEnd;
  const [pendingPlan, setPendingPlan] = useState(null);
  const [endDate, setEndDate] = useState(() => isoToDateKey(clinic.planEndDate));

  // Re-sync the draft when the clinic doc changes (refetch / payment card edits).
  useEffect(() => {
    setEndDate(isoToDateKey(clinic.planEndDate));
  }, [clinic.planEndDate]);

  const plans = getSystemPlansArray();
  const selected = plans.find((p) => p.code === overrides.planCode) || plans[0];
  const planDirty = overrides.planCode !== clinic.plan;
  const endDirty = endDate !== isoToDateKey(clinic.planEndDate);
  const dirty = planDirty || endDirty;

  const handlePick = (code) => {
    if (code === overrides.planCode) return;
    if (overrides.diffs.length > 0) setPendingPlan(code);
    else overrides.setPlanCode(code);
  };

  const handleSave = async () => {
    if (!dirty) return;
    try {
      if (planDirty) {
        await updatePlanDirectly({ clinicId: clinic.clinicId, planCode: overrides.planCode }).unwrap();
      }
      if (endDirty) {
        await setPlanEndDate({ clinicId: clinic.clinicId, planEndDate: endDate || null }).unwrap();
      }
      refetch?.();
      showSuccess(planDirty ? `המסלול עודכן ל־${selected.nameHebrew}` : "תאריך הסיום עודכן");
    } catch (error) {
      console.error("[CK-9814] Error saving clinic plan:", {
        errorCode: "CK-9814",
        errorMessage: error?.message,
        clinicId: clinic.clinicId,
        planCode: overrides.planCode,
        planDirty,
        endDirty,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בעדכון המסלול");
    }
  };

  return (
    <section className="ncd2-card" id="ncd2-block-plan">
      <header className="ncd2-card-head">
        <span className={`ncd2-plan-tile ${planToneClass(overrides.planCode)}`}>
          <Flag size={15} weight="fill" />
        </span>
        <div className="ncd2-card-titles">
          <span className="ncd2-card-eyebrow">מסלול</span>
          <h3 className="ncd2-card-title">
            {selected.nameHebrew}
            {selected.monthlyPrice > 0 && (
              <span className="ncd2-muted ncd2-plan-price">{formatCurrency(selected.monthlyPrice)}/חודש</span>
            )}
            {dirty && <span className="ncd2-badge ncd2-badge-muted">לא נשמר</span>}
          </h3>
        </div>
      </header>

      <div className="ncd2-plan-picker" role="radiogroup" aria-label="בחירת מסלול">
        {plans.map((p) => (
          <button
            key={p.code}
            type="button"
            role="radio"
            aria-checked={p.code === overrides.planCode}
            className={`ncd2-plan-chip ${planToneClass(p.code)}${p.code === overrides.planCode ? " is-active" : ""}`}
            onClick={() => handlePick(p.code)}
          >
            {p.code === overrides.planCode && <Check size={12} weight="bold" />}
            {p.nameHebrew}
          </button>
        ))}
      </div>

      <div className="ncd2-read-grid">
        <label className="ncd2-field ncd2-plan-end-field">
          <span className="ncd2-field-label">
            פעיל עד
            <span className="ncd2-muted">(אופציונלי)</span>
            <HoverTip text={PLAN_END_TOOLTIP}>
              <Info size={11} weight="fill" className="ncd2-inline-info" />
            </HoverTip>
          </span>
          <div className="ncd2-plan-end-row">
            <DatePicker value={endDate} onChange={(e) => setEndDate(e.target.value || "")} placeholder="ללא תאריך סיום" />
            {endDate && (
              <button
                type="button"
                className="ncd2-link-btn ncd2-plan-end-clear"
                onClick={() => setEndDate("")}
                aria-label="נקה תאריך סיום"
              >
                <X size={12} /> נקה
              </button>
            )}
          </div>
          <span className="ncd2-muted ncd2-hint">
            {endDate ? "בתאריך זה הקליניקה תחזור אוטומטית לניסיון" : "ריק = המסלול נשאר פעיל ללא הגבלת זמן"}
          </span>
        </label>
        {clinic.previousPaidPlan && (
          <div className="ncd2-read-row">
            <span className="ncd2-read-label">חזרה לניסיון</span>
            <span className="ncd2-read-value">
              מ־{SYSTEM_PLANS[clinic.previousPaidPlan]?.nameHebrew || clinic.previousPaidPlan}
              {clinic.planRevertedAt ? ` · ${formatDate(clinic.planRevertedAt)}` : ""}
            </span>
          </div>
        )}
      </div>

      {selected.featuresListHebrew?.length > 0 && (
        <ul className="ncd2-plan-features">
          {selected.featuresListHebrew.map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      )}

      <button type="button" className="ncd2-btn ncd2-btn-primary" onClick={handleSave} disabled={isLoading || !dirty}>
        <FloppyDisk size={16} /> {isLoading ? "שומר…" : "שמור מסלול"}
      </button>

      <ConfirmDialog
        isOpen={!!pendingPlan}
        onClose={() => setPendingPlan(null)}
        onConfirm={() => overrides.setPlanCode(pendingPlan, { keepOverrides: false })}
        title="החלפת מסלול"
        message={`לקליניקה יש ${overrides.diffs.length} חריגות מהמסלול הנוכחי. להחיל את ברירות המחדל של ${SYSTEM_PLANS[pendingPlan]?.nameHebrew || ""}, או לשמור את הערכים הקיימים כחריגות?`}
        confirmText="החל ברירות מחדל"
        cancelText="ביטול"
        type="warning"
      >
        <button
          type="button"
          className="ncd2-link-btn"
          onClick={() => {
            overrides.setPlanCode(pendingPlan, { keepOverrides: true });
            setPendingPlan(null);
          }}
        >
          שמור את הערכים הקיימים כחריגות
        </button>
      </ConfirmDialog>
    </section>
  );
}
